import { z } from 'zod';

// The strategy grouping shown in a packet's Creative section.
//
// Deliberately minimal — every field here is either displayed or acted on.
// Absent by design:
//   * the ungrouped list: the frontend has both inputs and derives it, which is
//     also the safer failure mode (a creative missing from every group shows up
//     rather than vanishing)
//   * sprintPacketID on a group: the route identifies the packet
//   * sortOrder: array order carries it (the server still ORDERs BY sort_order)
//   * generatedAt / token / startedAt: internal to the generation state machine
export const packetStrategyStatusSchema = z.enum([
  'idle',
  'generating',
  'ready',
  'failed',
]);

export type PacketStrategyStatus = z.infer<typeof packetStrategyStatusSchema>;

export const packetStrategyAdSchema = z.object({
  creativeID: z.number(),
  pinned: z.boolean(),
});

export type PacketStrategyAd = z.infer<typeof packetStrategyAdSchema>;

export const packetStrategyGroupSchema = z.object({
  id: z.number(),
  label: z.string(),
  blurb: z.string().nullable(),
  locked: z.boolean(),
  ads: z.array(packetStrategyAdSchema),
});

export type PacketStrategyGroup = z.infer<typeof packetStrategyGroupSchema>;

export const packetStrategySchema = z.object({
  status: packetStrategyStatusSchema,
  error: z.string().nullable(),
  groups: z.array(packetStrategyGroupSchema),
});

export type PacketStrategy = z.infer<typeof packetStrategySchema>;

// Persisted generation state. One state machine, always written whole.
export const packetStrategyGenerationSchema = z.object({
  status: packetStrategyStatusSchema,
  token: z.string().optional(),
  startedAt: z.string().optional(),
  error: z.string().optional(),
});

export type PacketStrategyGeneration = z.infer<
  typeof packetStrategyGenerationSchema
>;

// Editing a group's prose locks it — the server forces `locked`, the client
// never sends it.
export const UpdatePacketStrategyGroupDTO = z
  .object({
    label: z.string().min(1).max(120),
    blurb: z.string().max(400).nullable(),
  })
  .partial()
  .refine(
    (value) => value.label !== undefined || value.blurb !== undefined,
    'Provide label or blurb',
  );

export type UpdatePacketStrategyGroup = z.infer<
  typeof UpdatePacketStrategyGroupDTO
>;

// `groupID` is not nullable: Ungrouped means "arrived since the last
// generation", so it is not a manual destination. Moving an ad there would
// delete the membership row and clear the pin, so the move could not stick.
// Unpinning is the separate action (DELETE .../ads/:creativeID/pin).
export const MovePacketStrategyAdDTO = z.object({
  groupID: z.number(),
});

export type MovePacketStrategyAd = z.infer<typeof MovePacketStrategyAdDTO>;

export const UpdatePacketStrategyJustificationDTO = z.object({
  justification: z.string().max(2000).nullable(),
});

export type UpdatePacketStrategyJustification = z.infer<
  typeof UpdatePacketStrategyJustificationDTO
>;
